import type { CrmEmbedFlowType } from './crm-embed-types'

export interface CrmEmbedFlowCopy {
  shortName: string
  eyebrow: string
  activationTitle: string
  activationDescription: string
  activationButton: string
  congratsTitle: string
  congratsMessage: string
  experienceTitle: string
  experienceDescription: string
  sampleStep: string
  sampleTitle: string
  sampleDescription: string
  sampleSingular: string
  samplePlural: string
  shareTitle: string
  shareDescription: string
  addressTitle: string
  addressDescription: string
  processName: string
  completionTitle: string
  completionMessage: string
  /** 暂停亚马逊评论：第一页表单提交后的提示标题 */
  pausedTitle: string
  /** 暂停亚马逊评论：第一页表单提交后的提示正文 */
  pausedMessage: string
}

const copies: Record<CrmEmbedFlowType, CrmEmbedFlowCopy> = {
  warranty: {
    shortName: 'Warranty',
    eyebrow: '',
    activationTitle: 'Activate Your Warranty',
    activationDescription: 'Activate your warranty and enjoy more benefits.',
    activationButton: 'Activate Warranty',
    congratsTitle: 'Your Warranty Has Been Activated',
    congratsMessage:
      'Congratulations! You can now additionally apply for one Extra Accessory — no shipping fee and no extra cost.',
    experienceTitle: 'Tell Us About Your Warranty Product',
    experienceDescription:
      'Share an honest rating and comment about your warranty product.',
    sampleStep: 'Sample',
    sampleTitle: 'Choose Your Extra Accessories',
    sampleDescription:
      'Select one extra accessory configured for this product. Availability comes directly from this website.',
    sampleSingular: 'extra accessory',
    samplePlural: 'extra accessories',
    shareTitle: 'Share Your Warranty Product Experience',
    shareDescription: 'Use the comment you already wrote and complete the review step when required.',
    addressTitle: 'Confirm Your Extra Accessories Shipping Address',
    addressDescription:
      'Review your warranty product and selected extra accessories, then tell us where they should be sent.',
    processName: 'warranty activation',
    completionTitle: 'Congratulations!',
    completionMessage:
      'You have completed your warranty activation. We will confirm your Extra Accessory and ship it to you as soon as possible.',
    pausedTitle: 'Your Warranty Has Been Activated',
    pausedMessage:
      'Your warranty has been activated successfully. This review campaign is currently paused, so no further steps are needed. You can return to the homepage.',
  },
  omb: {
    shortName: 'OMB Claim',
    eyebrow: '',
    activationTitle: 'Register Your Order and Claim One More Bottle',
    activationDescription:
      'Enter your purchase details to start the One More Bottle claim for your eligible product.',
    activationButton: 'Start OMB Claim',
    congratsTitle: 'Your One More Bottle Claim Has Been Started',
    congratsMessage:
      'Congratulations! You can now additionally apply for one One More Bottle — no shipping fee and no extra cost.',
    experienceTitle: 'Tell Us About Your OMB Product',
    experienceDescription:
      'Share an honest rating and comment about your One More Bottle product.',
    sampleStep: 'One More Bottle',
    sampleTitle: 'Choose Your One More Bottle',
    sampleDescription: 'Select one available One More Bottle option configured for this product.',
    sampleSingular: 'One More Bottle option',
    samplePlural: 'One More Bottle options',
    shareTitle: 'Share Your Product Experience',
    shareDescription: 'Use the comment you already wrote and complete the review step when required.',
    addressTitle: 'Confirm Your One More Bottle Shipping Address',
    addressDescription:
      'Review your purchased product and selected One More Bottle option, then tell us where it should be sent.',
    processName: 'One More Bottle claim',
    completionTitle: 'Congratulations!',
    completionMessage:
      'You have completed your One More Bottle claim. We will confirm your OMB claim and ship it to you as soon as possible.',
    pausedTitle: 'This Campaign Has Been Paused',
    pausedMessage:
      'The inventory is no longer available and the campaign has been paused. Thank you for your interest — you can return to the homepage.',
  },
  registry: {
    shortName: 'Registry',
    eyebrow: '',
    activationTitle: 'Register Your Product',
    activationDescription:
      'Enter your purchase details to register your product and continue to the available registry benefits.',
    activationButton: 'Register Product',
    congratsTitle: 'Your Product Has Been Registered',
    congratsMessage:
      'Congratulations! You can now additionally apply for one Extra Accessory — no shipping fee and no extra cost.',
    experienceTitle: 'Tell Us About Your Registered Product',
    experienceDescription:
      'Share an honest rating and comment about your registered product.',
    sampleStep: 'Sample',
    sampleTitle: 'Choose Your Registry Sample',
    sampleDescription:
      'Select one registry sample configured for this product. Availability comes directly from this website.',
    sampleSingular: 'registry sample',
    samplePlural: 'registry samples',
    shareTitle: 'Share Your Registered Product Experience',
    shareDescription: 'Use the comment you already wrote and complete the review step when required.',
    addressTitle: 'Confirm Your Registry Sample Shipping Address',
    addressDescription:
      'Review your registered product and selected sample, then tell us where the registry sample should be sent.',
    processName: 'product registry',
    completionTitle: 'Congratulations!',
    completionMessage:
      'You have completed your product registration. We will confirm your Extra Accessory and ship it to you as soon as possible.',
    pausedTitle: 'Your Order Has Been Registered',
    pausedMessage:
      'Your order has been registered successfully. This review campaign is currently paused, so no further steps are needed. You can return to the homepage.',
  },
  ryo: {
    shortName: 'RYO Claim',
    eyebrow: '',
    activationTitle: 'Register Your Order and Join the Club',
    activationDescription:
      'Enter your purchase details to register your order, join our VIP Club, and claim one of six collectible plush dolls.',
    activationButton: 'Start RYO Claim',
    congratsTitle: 'Your Order Is Registered — Welcome to the Club',
    congratsMessage:
      'You have registered your order and joined our VIP Club. Complete the 3 simple steps below to receive your plush doll.',
    experienceTitle: 'Tell Us About Your Neatique Product',
    experienceDescription:
      'Share an honest rating and comment about the product from this order.',
    sampleStep: 'Plush Doll',
    sampleTitle: 'Choose Your Plush Doll',
    sampleDescription:
      'Select one of the six collectible plush dolls. It will be shipped together with your claim.',
    sampleSingular: 'plush doll',
    samplePlural: 'plush dolls',
    shareTitle: 'Share Your Product Experience',
    shareDescription: 'Use the comment you already wrote and complete the review step when required.',
    addressTitle: 'Confirm Your Plush Doll Shipping Address',
    addressDescription:
      'Review your order and selected plush doll, then tell us where it should be sent.',
    processName: 'RYO claim',
    completionTitle: 'Congratulations!',
    completionMessage:
      'You have completed all the steps. We will confirm your plush doll and ship it to you as soon as possible.',
    pausedTitle: 'This Campaign Has Been Paused',
    pausedMessage:
      'This campaign has been paused for now and no further steps are needed. You can return to the homepage.',
  },
}

export const getCrmEmbedFlowCopy = (flowType: CrmEmbedFlowType) => copies[flowType]

/** 暂停亚马逊评论时的提示文案（第一页表单提交完成后展示）。 */
export const getCrmPausedAmazonReviewsCopy = (flowType: CrmEmbedFlowType) => {
  const copy = copies[flowType]
  return { title: copy.pausedTitle, message: copy.pausedMessage }
}

// ---------------------------------------------------------------------------
// 赠品名称（客服流程中的赠品文案，可自定义；embed 自包含，不依赖外部模块）
// ---------------------------------------------------------------------------

/** 赠品名称默认值（未配置时的客服流程赠品文案）。 */
const CRM_GIFT_NAME_DEFAULT = 'Extra Accessory'

/** 赠品名称复数形式（尽力而为）：s/x/z/ch/sh 结尾加 es，辅音+y 变 ies，其余加 s。 */
const crmEmbedGiftNamePlural = (name: string): string => {
  const trimmed = String(name || '').trim()
  if (!trimmed) return trimmed
  const lower = trimmed.toLowerCase()
  if (/(s|x|z|ch|sh)$/.test(lower)) return `${trimmed}es`
  if (/[^aeiou]y$/.test(lower)) return `${trimmed.slice(0, -1)}ies`
  return `${trimmed}s`
}

/** 将文案中的 "extra accessory" / "extra accessories"（不区分大小写）替换为赠品名称。 */
const applyCrmEmbedGiftName = (text: string, giftName: string): string => {
  const name = String(giftName || '').trim() || CRM_GIFT_NAME_DEFAULT
  if (name === CRM_GIFT_NAME_DEFAULT) return String(text ?? '')
  const plural = crmEmbedGiftNamePlural(name)
  return String(text ?? '')
    .replace(/\bExtra Accessories\b/gi, plural)
    .replace(/\bExtra Accessory\b/gi, name)
}

/**
 * 获取客服流程文案，并按站点配置的赠品名称替换 "Extra Accessory" 相关文案。
 * 未配置（默认 Extra Accessory）时原样返回；OMB 流程不含该文案，不受影响。
 */
export const getCrmEmbedFlowCopyWithGiftName = (
  flowType: CrmEmbedFlowType,
  giftName = '',
): CrmEmbedFlowCopy => {
  const base = copies[flowType]
  const name = String(giftName || '').trim() || CRM_GIFT_NAME_DEFAULT
  if (name === CRM_GIFT_NAME_DEFAULT) return base
  return {
    shortName: applyCrmEmbedGiftName(base.shortName, name),
    eyebrow: applyCrmEmbedGiftName(base.eyebrow, name),
    activationTitle: applyCrmEmbedGiftName(base.activationTitle, name),
    activationDescription: applyCrmEmbedGiftName(base.activationDescription, name),
    activationButton: applyCrmEmbedGiftName(base.activationButton, name),
    congratsTitle: applyCrmEmbedGiftName(base.congratsTitle, name),
    congratsMessage: applyCrmEmbedGiftName(base.congratsMessage, name),
    experienceTitle: applyCrmEmbedGiftName(base.experienceTitle, name),
    experienceDescription: applyCrmEmbedGiftName(base.experienceDescription, name),
    sampleStep: applyCrmEmbedGiftName(base.sampleStep, name),
    sampleTitle: applyCrmEmbedGiftName(base.sampleTitle, name),
    sampleDescription: applyCrmEmbedGiftName(base.sampleDescription, name),
    sampleSingular: applyCrmEmbedGiftName(base.sampleSingular, name),
    samplePlural: applyCrmEmbedGiftName(base.samplePlural, name),
    shareTitle: applyCrmEmbedGiftName(base.shareTitle, name),
    shareDescription: applyCrmEmbedGiftName(base.shareDescription, name),
    addressTitle: applyCrmEmbedGiftName(base.addressTitle, name),
    addressDescription: applyCrmEmbedGiftName(base.addressDescription, name),
    processName: applyCrmEmbedGiftName(base.processName, name),
    completionTitle: applyCrmEmbedGiftName(base.completionTitle, name),
    completionMessage: applyCrmEmbedGiftName(base.completionMessage, name),
    pausedTitle: base.pausedTitle,
    pausedMessage: base.pausedMessage,
  }
}
